import { useQuery } from '@apollo/client'
import { useIsAuthenticated } from '@azure/msal-react'
import Reminder from '@src/common/reminder/Reminder'
import stationStore from '@src/domain/station/stationStore'
import _ from 'lodash'
import { observer } from 'mobx-react'
import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { DisplayedReminder } from '../../common/reminder/types'
import { getStationReminders } from '../admin/reminders/RemindersQueries'
import reminderStore, { SnoozedReminder } from './ReminderStore'

type ReminderType = {
  id: string
  time: string
  name: string
  weekdays: string[]
  muted?: boolean
  disabled?: boolean
}

const Reminders: FC = observer(({ children }) => {
  const activeStationId = stationStore.activeStationId
  const [currentReminders, setCurrentReminders] = useState<DisplayedReminder[]>([])
  const closeModal = () => {
    setCurrentReminders([])
  }
  const snoozedReminders = reminderStore.snoozedReminders

  const snooze = (snoozeInMinutes: number) => {
    const now = moment(new Date())
    const nextReminderTime = moment(now).add(snoozeInMinutes, 'minutes').startOf('minute')
    const snoozedReminders = currentReminders.map((reminder) => ({
      nextRemindTime: nextReminderTime,
      name: reminder.name,
      id: reminder.id,
      muted: reminder.muted,
    }))
    reminderStore.addSnoozedReminders(snoozedReminders)
    setCurrentReminders([])
  }

  const isAuthenticated = useIsAuthenticated()
  const { data } = useQuery(getStationReminders, {
    variables: { stationId: activeStationId },
    pollInterval: 300000, // Refetch results every 5 minutes
    skip: !isAuthenticated || activeStationId === null || activeStationId === undefined,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const stationReminders: ReminderType[] = data?.getStationReminders ?? []
    const chainReminders: ReminderType[] = data?.getStationChainReminders ?? []
    const remindersToday = getTodaysReminders(stationReminders)
    const chainRemindersToday = getTodaysReminders(chainReminders)
    const mappedSnoozedReminders = (snoozedReminders ?? []).map(snoozedReminderToReminderType)
    const now = moment(new Date())
    const date = now.format('YYYY-MM-DD')
    const remindersInFuture = remindersToday
      .concat(chainRemindersToday)
      .concat(mappedSnoozedReminders)
      .filter((reminder) => {
        const end = moment(`${date} ${reminder.time}`)
        const durationInMilliseconds = moment.duration(end.diff(now)).asMilliseconds()
        return durationInMilliseconds > 0
      })
    const groupedByTime = _.groupBy(remindersInFuture, 'time')
    const timeoutIds = Object.entries(groupedByTime)
      .map(([time, reminders]) => {
        const end = moment(`${date} ${time}`)
        const durationInMilliseconds = moment.duration(end.diff(now)).asMilliseconds()
        if (durationInMilliseconds > 0) {
          const timeout = setTimeout(() => {
            setCurrentReminders(reminders)
          }, durationInMilliseconds)
          return timeout
        }
        return null
      })
      .filter((timeout) => !!timeout)
    return () => {
      for (const timeout of timeoutIds) {
        clearTimeout(timeout!)
      }
    }
  }, [data, snoozedReminders])

  return (
    <>
      <Reminder
        reminders={currentReminders}
        closeModal={closeModal}
        snooze={snooze}
        isMuted={reminderStore.remindersMuted}
      />
      {children}
    </>
  )
})

function getTodaysReminders(reminders: ReminderType[]) {
  const now = new Date()
  const weekdayFormat = new Intl.DateTimeFormat('en-GB', { weekday: 'short' })
  const weekDayNow = weekdayFormat.format(now).toLowerCase()
  return reminders.filter((reminder) => reminder.weekdays.includes(weekDayNow) && !reminder.disabled)
}

function snoozedReminderToReminderType(snoozedReminder: SnoozedReminder): ReminderType {
  return {
    id: snoozedReminder.id,
    time: moment(snoozedReminder.nextRemindTime).format('HH:mm'),
    name: snoozedReminder.name,
    muted: snoozedReminder.muted,
    // Weekdays are not used in snoozed reminders but required in ReminderType. Not used, so empty array is OK.
    weekdays: [],
  }
}

export default Reminders
